import { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { GithubProject } from '../data/githubProjects';
import { githubProjects } from '../data/githubProjects';
import type { RealWorldProject } from '../data/realWorldProjects';
import { realWorldProjects } from '../data/realWorldProjects';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faHammer } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { getCategoryColor, getCategoryBorderColor } from '../utils/skillCategoryColors';

const Projects: FC = () => {
  const getSkillCategory = (tech: string): string => {
    if (['React', 'TypeScript', 'Redux', 'Next.js', 'JavaScript', 'HTML', 'CSS', 'Vue', 'Angular'].includes(tech)) {
      return 'Frontend';
    }
    if (['Node.js', 'Python', 'FastAPI', 'Express', 'Java', 'C++', 'C#', 'PHP', 'Ruby', 'Go'].includes(tech)) {
      return 'Backend';
    }
    if (['CI/CD', 'AWS', 'Docker', 'Kubernetes', 'Jenkins', 'Terraform', 'Ansible', 'Git', 'Linux', 'Vite', 'Playwright', 'Jest'].includes(tech)) {
      return 'DevOps';
    }
    if (['Leadership', 'Communication', 'Problem Solving', 'Mentoring', 'Team Management', 'Agile', 'Scrum'].includes(tech)) {
      return 'Soft Skills';
    }
    return 'Other';
  };

  return (
    <ProjectsSection id="projects" className="section section-dark">
      <div className="container">
        <Subsection>
          <SubsectionTitle id="software-projects">Software projects</SubsectionTitle>
          <SubsectionLead>Apps, tools, and products—mostly TypeScript and React.</SubsectionLead>
          <ProjectsGrid>
            {githubProjects.map((project: GithubProject) => (
              <ProjectCard key={project.id}>
                <ProjectImage src={project.image} alt={project.title} />
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <TechnologiesContainer>
                    {project.technologies.map((tech: string, index: number) => (
                      <Technology key={index} category={getSkillCategory(tech)}>
                        {tech}
                      </Technology>
                    ))}
                  </TechnologiesContainer>
                  <LinksContainer>
                    <ProjectLink
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View ${project.title} on GitHub`}
                    >
                      <FontAwesomeIcon icon={faGithub} /> GitHub
                    </ProjectLink>
                    {project.demoUrl && (
                      <ProjectLink
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View live demo of ${project.title}`}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Live Demo
                      </ProjectLink>
                    )}
                  </LinksContainer>
                </ProjectContent>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        </Subsection>

        <Subsection $alternate>
          <SubsectionTitle id="real-world-projects">Real world projects</SubsectionTitle>
          <SubsectionLead>Woodworking, building, and other offline work—photos and short write-ups.</SubsectionLead>
          <ProjectsGrid>
            {realWorldProjects.map((project: RealWorldProject) => (
              <ProjectCard key={project.id}>
                <ProjectImage src={project.cardImage} alt={project.title} />
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.summary}</ProjectDescription>
                  {project.tags && project.tags.length > 0 && (
                    <MakerTags>
                      {project.tags.map((tag) => (
                        <MakerTag key={tag}>{tag}</MakerTag>
                      ))}
                    </MakerTags>
                  )}
                  <LinksContainer>
                    <DetailLink to={`/make/${project.id}`}>
                      <FontAwesomeIcon icon={faHammer} /> Photos &amp; write-up
                    </DetailLink>
                  </LinksContainer>
                </ProjectContent>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        </Subsection>
      </div>
    </ProjectsSection>
  );
};

const ProjectsSection = styled.section`
  padding: 50px 0;
  background-color: #f8f9fa;
`;

const Subsection = styled.div<{ $alternate?: boolean }>`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }

  ${(p) =>
    p.$alternate &&
    `
    padding-top: 2.5rem;
    border-top: 1px solid #dee2e6;
  `}
`;

const SubsectionTitle = styled.h2`
  font-size: 2.25rem;
  text-align: center;
  margin-bottom: 0.5rem;
  color: #2c3e50;
  scroll-margin-top: 5.5rem;
  font-weight: 700;
`;

const SubsectionLead = styled.p`
  text-align: center;
  color: #666;
  max-width: 560px;
  margin: 0 auto 2rem;
  line-height: 1.5;
  font-size: 1rem;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: contain;
  object-position: center;
  background-color: #eef1f4;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.25rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
`;

const ProjectDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const TechnologiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Technology = styled.span<{ category: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  background: ${(props) => getCategoryColor(props.category)};
  color: #2c3e50;
  border: 1px solid ${(props) => getCategoryBorderColor(props.category)};
  transition: all 0.2s ease-in-out;
`;

const MakerTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const MakerTag = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  background: #e8eef2;
  color: #2c3e50;
  border: 1px solid #cfd8dc;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: #2c3e50;
  color: white;
  text-decoration: none;
  font-size: 0.875rem;
  transition: background-color 0.3s ease;

  &:hover {
    background: #34495e;
  }

  svg {
    font-size: 1rem;
  }
`;

const DetailLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: #2c3e50;
  color: white;
  text-decoration: none;
  font-size: 0.875rem;
  transition: background-color 0.3s ease;

  &:hover {
    background: #34495e;
  }

  svg {
    font-size: 1rem;
  }
`;

export default Projects;
